import { MetaAuthFailureReason } from '../../extViewmodels/sdk/models/Ads';

/** `category` on an OperationResult's `error` when Meta rejected the user's token. */
const META_AUTH_FAILURE_ERROR_CATEGORY = 'MetaAuthFailureReason';

/** Where a customer clears a checkpoint or confirms their account. */
export const FACEBOOK_LOGIN_URL = 'https://www.facebook.com';

/**
 * Customer guidance per reason. Mirrors the API's `MetaAuthFailure.CustomerMessage` so the emails and the UI
 * say the same thing; change both together.
 */
export const META_AUTH_FAILURE_MESSAGES: Record<MetaAuthFailureReason, string> = {
	[MetaAuthFailureReason.CheckpointRequired]:
		'Facebook has paused your account until you complete a security check. Log in at facebook.com and follow the prompts, then reconnect here if it still shows as disconnected.',
	[MetaAuthFailureReason.AccountUnconfirmed]:
		'Facebook needs you to confirm your account. Log in at facebook.com to finish, then reconnect.',
	[MetaAuthFailureReason.PasswordChanged]:
		'Your Facebook password changed, which signed Levitate out. Reconnect to restore access.',
	[MetaAuthFailureReason.TokenExpired]: 'Your Facebook connection expired. Reconnect to restore access.',
	[MetaAuthFailureReason.AppRemoved]:
		"Levitate's access was removed in your Facebook settings. Reconnect to grant it again.",
	[MetaAuthFailureReason.SessionInvalidated]: "Facebook ended Levitate's access. Reconnect to restore it.",
};

const FACEBOOK_LOGIN_FIRST_REASONS: ReadonlySet<MetaAuthFailureReason> = new Set([
	MetaAuthFailureReason.CheckpointRequired,
	MetaAuthFailureReason.AccountUnconfirmed,
]);

/**
 * Whether the customer has to fix the account on facebook.com before reconnecting can help.
 * @param reason The reason Meta rejected the token.
 */
export const requiresFacebookLogin = (reason: MetaAuthFailureReason): boolean =>
	FACEBOOK_LOGIN_FIRST_REASONS.has(reason);

/**
 * Narrows an API value to a known reason, so a reason the API adds later is ignored rather than rendered blank.
 * @param value The raw value from the API.
 */
export const asMetaAuthFailureReason = (value: unknown): MetaAuthFailureReason | undefined =>
	Object.values(MetaAuthFailureReason).includes(value as MetaAuthFailureReason)
		? (value as MetaAuthFailureReason)
		: undefined;

/**
 * Reads the reason from a failed OperationResult thrown by the API client. The reason name is in
 * `error.errorMessage`; `error.errorCode` is the enum's number, which the API does not promise to keep stable.
 * Undefined for any other failure.
 * @param error The thrown OperationResult, or anything else a request can reject with.
 */
export const getMetaAuthFailureReason = (error: unknown): MetaAuthFailureReason | undefined => {
	const technicalError = (error as { error?: { category?: string; errorMessage?: string } } | null)?.error;
	if (technicalError?.category !== META_AUTH_FAILURE_ERROR_CATEGORY) {
		return undefined;
	}
	return asMetaAuthFailureReason(technicalError.errorMessage);
};

/**
 * The customer guidance for a failed OperationResult that Meta's token rejection caused; undefined for any other failure.
 * @param error The thrown OperationResult, or anything else a request can reject with.
 */
export const getMetaAuthFailureMessage = (error: unknown): string | undefined => {
	const reason = getMetaAuthFailureReason(error);
	return reason ? META_AUTH_FAILURE_MESSAGES[reason] : undefined;
};
